// step2-canvas.js — Step 2 Assignment Canvas
// Extracted from script.js. Contains state variables, sortable instances,
// all Step 2 VBR/cloud assignment canvas functions, and event wiring.
//
// Dependencies:
//   config.js    — effortConfig, blockHours
//   state.js     — sites
//   utils.js     — escapeHtml, isRepoMigrationWorkload, isVb365ManagedWorkload,
//                  isVbfCloudManagedWorkload, isProtectedTbWorkload, getVbfCloudProvider,
//                  CLOUD_PROVIDER_CONFIGS
//   recalc.js    — scheduleRenderEstimate, getGfChipIcon
//   helpers.js   — destroyStep2Sortables, getSiteById, getCanvasEngagementFlags,
//                  getGfWorkloadLabel, getGfWorkloadUnit, isNonVbrWorkload,
//                  getS2ReuseableVbrs, normalizeSourceWorkloadForS2,
//                  getS2MigrationWorkloads, buildS2NewVbrForm, getS2KastenWorkloads
//
// Window globals (bridged from script.js):
//   GF_SVG_GRIP_SM, GF_SVG_PLUS, GF_SVG_CLOSE, GF_SVG_PENCIL, GF_SVG_SERVER
//   currentStep, totalSteps, output
//   renderSiteList, renderEmptyOutput, syncCanvasStateDefaults

import Sortable from 'sortablejs';
import { emit, BUS_EVENTS } from './bus.js';
import { effortConfig, blockHours } from './config.js';
import { sites } from './state.js';
import { jobTypeOptions } from './workload-defs.js';
import { escapeHtml, isRepoMigrationWorkload, isVb365ManagedWorkload,
         isVbfCloudManagedWorkload, isProtectedTbWorkload, getVbfCloudProvider,
         CLOUD_PROVIDER_CONFIGS } from './utils.js';
import { scheduleRenderEstimate, getGfChipIcon } from './recalc.js';
import { destroyStep2Sortables, getSiteById, getCanvasEngagementFlags,
         getGfWorkloadLabel, getGfWorkloadUnit, isNonVbrWorkload,
         getS2ReuseableVbrs, normalizeSourceWorkloadForS2,
         getS2MigrationWorkloads, buildS2NewVbrForm,
         getS2KastenWorkloads } from './helpers.js';

// ── Step 2 Assignment Canvas ───────────────────────────────────────────────────
// Module-level reference to the click-outside handler so we can remove it on re-wire / hide.
let _s2CollapseConfigHandler = null;
// NOTE: These declarations MUST appear before the initialization calls below,
// because renderSiteList() → needsNewVbrDeployment() accesses step2VbrLanes.

/**
 * VBR lanes explicitly added by the user in Step 2.
 * Each lane:
 *   { laneId, type: 'existing'|'new', sourceInstanceId?, name, siteId, siteName,
 *     deploymentType: 'virtual'|'physical', osPlatform: 'windows'|'linux' }
 * Declared here (before any function that may call needsNewVbrDeployment) to
 * avoid a temporal dead zone ReferenceError during early initialisation.
 */
// Step 2 VBR lanes — declared here (before init calls) to avoid temporal dead zone.
// See script.js comment for context. window bridge maintained for recalc.js access.
export let step2VbrLanes = [];
window.step2VbrLanes = step2VbrLanes;
export let step2CloudLanes = [];
window.step2CloudLanes = step2CloudLanes;
export let step2Vb365Lanes = [];
window.step2Vb365Lanes = step2Vb365Lanes;
export let s2NextLaneId = 1;

/** Which Step 2 placement picker panel is open: null | 'reuse' | 'new' */
export let s2ActivePicker = null;

/** Which VB365 picker panel is open: null | 'reuse' | 'new' */
let s2ActiveVb365Picker = null;

/** Workload-to-lane assignment map: { [rowId]: laneId } */
export let workloadAssignments = {};

// Sortable instances for the Step 2 canvas
let s2SortableTray = null;
window.s2SortableTray = s2SortableTray;
const s2SortableLanes = [];
window.s2SortableLanes = s2SortableLanes;

/**
 * Assignments: rowId (number) → laneId (string).
 * A workload not present in this map is "unassigned" (sits in the tray).
 */

/** Resets Step 2 canvas state — call when starting a new estimate or when Step 1 changes significantly. */
function resetStep2Canvas() {
    workloadAssignments = {};
    step2VbrLanes   = [];
    window.step2VbrLanes = step2VbrLanes;
    step2CloudLanes = [];
    window.step2CloudLanes = step2CloudLanes;
    step2Vb365Lanes = [];
    window.step2Vb365Lanes = step2Vb365Lanes;
    s2NextLaneId    = 1;
    s2ActivePicker  = null;
    s2ActiveVb365Picker = null;
    destroyStep2Sortables();
}

/**
 * Returns existing VBRs from Step 1 that have not yet been added as lanes.
 *
 * Two sources are checked:
 *  1. Named instances in site.veeamServers (type === 'vbr') — added via the server badge path.
 *  2. Count-based entries in site.existingEnvironment.vbrServers — added via the count-chip path.
 *     These get a synthetic instanceId of the form "env-vbr-{siteId}-{index}".
 */

/** Adds an existing VBR from Step 1 as a Step 2 lane. */
function addS2ExistingVbrLane(instanceId) {
    // Find the entry from the reuseable list (handles both named and count-based VBRs)
    const entry = getS2ReuseableVbrs().find((e) => e.instanceId === instanceId)
        // Also include already-used ones so a direct call doesn't silently fail
        ?? (() => {
            // Fallback: search named instances
            for (const site of sites) {
                const sv = (site.veeamServers || []).find((s) => s.type === 'vbr' && s.instanceId === instanceId);
                if (sv) return { instanceId: sv.instanceId, name: sv.name || 'VBR Server', siteId: site.id, siteName: site.name || `Site ${site.id}` };
            }
            return null;
        })();
    if (!entry) return;
    step2VbrLanes.push({
        laneId:           String(s2NextLaneId++),
        type:             'existing',
        sourceInstanceId: entry.instanceId,
        name:             entry.name ? `${entry.name} (Reused)` : 'Reused VBR Server',
        siteId:           entry.siteId,
        siteName:         entry.siteName,
    });
    s2ActivePicker = null;
    renderStep2Canvas();
}

/** Adds a brand-new VBR lane for Step 2 future-state deployment. */
function addS2NewVbrLane(name, siteId) {
    const site = getSiteById(siteId);
    // Auto-generate "VBR 1", "VBR 2", … based on how many new-type lanes exist
    const newLaneCount = step2VbrLanes.filter((l) => l.type === 'new').length + 1;
    step2VbrLanes.push({
        laneId:          String(s2NextLaneId++),
        type:            'new',
        name:            name || `New VBR Server ${newLaneCount}`,
        siteId:          siteId,
        siteName:        site ? (site.name || `Site ${siteId}`) : String(siteId),
        deploymentType:  'virtual',
        osPlatform:      'linux',
        vsaHa:           false,
        configExpanded:  true,
        step2Only:       site ? site.step2Only === true : false,
    });
    s2ActivePicker = null;
    renderStep2Canvas();
}

/** Removes a lane and un-assigns any workloads in it. */
function removeS2Lane(laneId) {
    step2VbrLanes = step2VbrLanes.filter((l) => l.laneId !== laneId);
    window.step2VbrLanes = step2VbrLanes;
    Object.keys(workloadAssignments).forEach((rowId) => {
        if (workloadAssignments[rowId] === laneId) delete workloadAssignments[rowId];
    });
    renderStep2Canvas();
    emit(BUS_EVENTS.VALIDATION_SYNC_NEEDED);
}

/**
 * Offset applied to unprotectedWorkload ids so they don't collide with
 * site.workloads rowIds (both counters start at 1) inside workloadAssignments.
 */
const S2_UNPROTECTED_ID_OFFSET = 1_000_000;
window.S2_UNPROTECTED_ID_OFFSET = S2_UNPROTECTED_ID_OFFSET;

/**
 * ID offset for hypervisor migration scopes so they don't collide with
 * site workload rowIds or unprotected workload rowIds inside workloadAssignments.
 */
const S2_MIGRATION_ID_OFFSET = 2_000_000;
window.S2_MIGRATION_ID_OFFSET = S2_MIGRATION_ID_OFFSET;

/**
 * ID offset for existing jobs so they don't collide with unprotected workload
 * rowIds or migration scope rowIds inside workloadAssignments in Step 2.
 */
const S2_JOB_ID_OFFSET = 3_000_000;
window.S2_JOB_ID_OFFSET = S2_JOB_ID_OFFSET;

/**
 * Normalise a hypervisorMigrationScopes entry to the shape the Step 2
 * chip builder and workloadAssignments keying expect.
 */

/** Migration scopes marked "Add to workload scope matrix" — also surfaced in VBR placement. */

/** Returns true if a workload should be excluded from the VBR canvas (handled by another product). */

/**
 * sourceWorkloads from site.sourceWorkloads that participate in the VBR drag system.
 * A sourceWorkload is draggable when:
 *  - It has been manually assigned, OR
 *  - No existing-VBR lane for its site matches its managedByServerId.
 * VB365-managed, VBfCloud-managed, and Kasten workloads are excluded (handled elsewhere).
 */
function getS2DraggableSourceWorkloads() {
    return sites.flatMap((site) =>
        (site.sourceWorkloads || [])
            .filter((sw) => {
                if (isNonVbrWorkload(sw.workloadValue)) return false;
                // Always include: auto-assigned (shows in lane drop zone) or unassigned (shows in tray)
                return true;
            })
            .map((sw) => normalizeSourceWorkloadForS2(sw, site))
    );
}

/** All workloads (migration targets + source workloads) not yet dragged into a VBR lane.
 *  VB365-managed and VBfCloud-managed workloads are excluded — they render in their own sections. */
function getS2UnassignedWorkloads() {
    const legacyWorkloads = sites.flatMap((site) =>
        (site.workloads || [])
            .filter((w) => w.value && !isRepoMigrationWorkload(w.value) && !isNonVbrWorkload(w.value) && !workloadAssignments[w.rowId])
            .map((w) => ({ ...w, siteName: site.name || `Site ${site.id}` }))
    );
    const migrationWorkloads = getS2MigrationWorkloads().filter((w) => !workloadAssignments[w.rowId]);
    const unassignedSource = getS2DraggableSourceWorkloads().filter((sw) => !workloadAssignments[sw.rowId]);
    return [...legacyWorkloads, ...migrationWorkloads, ...unassignedSource];
}

/** All VBR-placeable workloads in the tray (migration targets + source workloads; excludes VB365 and VBfCloud). */
function getS2AllUnprotectedWorkloads() {
    return [
        ...getS2MigrationWorkloads(),
        ...getS2DraggableSourceWorkloads(),
    ];
}

/** All unprotected workloads for a given VBfCloud provider. */
function getS2CloudAllWorkloads(provider) {
    return sites.flatMap((site) =>
        (site.sourceWorkloads || [])
            .map((sw) => normalizeSourceWorkloadForS2(sw, site))
            .filter((sw) => sw.value && getVbfCloudProvider(sw.value) === provider)
    );
}

/** Unprotected workloads for a VBfCloud provider that haven't been assigned to any lane yet. */
function getS2CloudUnassignedWorkloads(provider) {
    return getS2CloudAllWorkloads(provider).filter((w) => !workloadAssignments[w.rowId]);
}

/** Workloads assigned to a specific cloud appliance lane. */
function getS2CloudAssignedWorkloads(laneId) {
    return sites.flatMap((site) =>
        (site.sourceWorkloads || [])
            .map((sw) => normalizeSourceWorkloadForS2(sw, site))
            .filter((sw) => sw.value && isVbfCloudManagedWorkload(sw.value) && workloadAssignments[sw.rowId] === laneId)
    );
}

/** Adds a new cloud appliance lane for the given provider. */
function addS2CloudLane(provider, siteId) {
    const cfg  = CLOUD_PROVIDER_CONFIGS[provider];
    const site = getSiteById(siteId);
    if (!cfg) return;
    const count = step2CloudLanes.filter((l) => l.cloudProvider === provider).length + 1;
    step2CloudLanes.push({
        laneId:        String(s2NextLaneId++),
        cloudProvider: provider,
        name:          `${cfg.serverLabel} ${count}`,
        siteId:        siteId,
        siteName:      site ? (site.name || `Site ${siteId}`) : '',
    });
    s2ActivePicker = null;
    renderStep2Canvas();
}

/** Removes a cloud appliance lane and un-assigns its workloads. */
function removeS2CloudLane(laneId) {
    step2CloudLanes = step2CloudLanes.filter((l) => l.laneId !== laneId);
    window.step2CloudLanes = step2CloudLanes;
    Object.keys(workloadAssignments).forEach((rowId) => {
        if (workloadAssignments[rowId] === laneId) delete workloadAssignments[rowId];
    });
    renderStep2Canvas();
    emit(BUS_EVENTS.VALIDATION_SYNC_NEEDED);
}

/** Workloads managed by VB365 (SaaS group) from the source workloads list. */
function getS2Vb365Workloads() {
    return sites.flatMap((site) =>
        (site.sourceWorkloads || [])
            .map((sw) => normalizeSourceWorkloadForS2(sw, site))
            .filter((sw) => sw.value && isVb365ManagedWorkload(sw.value))
    );
}

/** VB365-managed workloads not yet assigned to a VB365 lane. */
function getS2Vb365UnassignedWorkloads() {
    return getS2Vb365Workloads().filter((w) => !workloadAssignments[w.rowId]);
}

/**
 * Returns true when the VB365 section has unplaced workloads that block navigation.
 * Uses the same placement math as the section header badge:
 *   - draggable tray items (getS2Vb365Workloads) must all be assigned, AND
 *   - protected-chip jobs count as placed (they don't need drag assignment).
 * This avoids false-positives when a VB365 job is displayed as a read-only
 * protected chip but getS2Vb365Workloads() mistakenly includes it in the tray.
 */
function getS2Vb365BlocksNavigation() {
    const draggable = getS2Vb365Workloads();
    const unassignedDraggable = draggable.filter((w) => !workloadAssignments[w.rowId]);
    if (unassignedDraggable.length === 0) return false;
    // If every unassigned draggable item is also shown as a protected chip in
    // some lane, treat it as placed — the badge considers it placed, so nav should too.
    const protectedJobRowIds = new Set(
        step2Vb365Lanes.flatMap((lane) =>
            getS2ProtectedVb365JobsForLane(lane).map((j) => S2_JOB_ID_OFFSET + (j.id || 0))
        )
    );
    const trulyUnassigned = unassignedDraggable.filter((w) => !protectedJobRowIds.has(w.rowId));
    return trulyUnassigned.length > 0;
}

/** Workloads (migration targets + source workloads) assigned to a specific VBR lane.
 *  VB365-managed and VBfCloud-managed workloads are excluded — they use their own lane systems. */
function getS2AssignedWorkloads(laneId) {
    const legacyWorkloads = sites.flatMap((site) =>
        (site.workloads || [])
            .filter((w) => w.value && !isRepoMigrationWorkload(w.value) && !isNonVbrWorkload(w.value) && workloadAssignments[w.rowId] === laneId)
            .map((w) => ({ ...w, siteName: site.name || `Site ${site.id}` }))
    );
    const migrationWorkloads = getS2MigrationWorkloads().filter((w) => workloadAssignments[w.rowId] === laneId);
    const assignedSource = getS2DraggableSourceWorkloads().filter((sw) => workloadAssignments[sw.rowId] === laneId);
    return [...legacyWorkloads, ...migrationWorkloads, ...assignedSource];
}

/** Returns a section header row with title + coloured status pill. */
function buildS2SectionHeader(svgIcon, title, assigned, total, noTarget = false) {
    let pillHtml;
    if (noTarget) {
        // Red: workloads exist but there is nowhere to assign them (no appliance added yet)
        const rem = total - assigned;
        pillHtml = `<span class="s2-status-pill s2-status-pill--error">${rem} workload${rem !== 1 ? 's' : ''} — add an appliance first</span>`;
    } else if (assigned >= total && total > 0) {
        pillHtml = `<span class="s2-status-pill s2-status-pill--ok">✓ All ${total} placed</span>`;
    } else if (total > 0) {
        pillHtml = `<span class="s2-status-pill s2-status-pill--warn">${assigned} of ${total} placed</span>`;
    } else {
        pillHtml = '';
    }
    return `<div class="s2-section-header">
        <span class="s2-section-header-icon" aria-hidden="true">${svgIcon}</span>
        <span class="s2-section-header-title">${escapeHtml(title)}</span>
        ${pillHtml}
    </div>`;
}

function buildS2WorkloadChip(workload, variant /* 'tray' | 'assigned' | 'protected' */, showSiteBadge = false) {
    const label        = getGfWorkloadLabel(workload.value);
    const icon         = getGfChipIcon(workload.value);
    const unit         = getGfWorkloadUnit(workload.value);
    const isUnstr      = isProtectedTbWorkload(workload.value);
    const qty          = isUnstr
        ? (typeof workload.protectedTb === 'number' ? workload.protectedTb : (workload.count || 0))
        : (workload.count || 0);
    const qtyText      = qty > 0 ? `× ${qty}${unit ? ' ' + unit : ''}` : '';
    const isReadOnly   = variant === 'protected';
    const chipClass    = variant === 'protected' ? 's2-wl-chip--protected' : 's2-wl-chip--unprotected';
    const gripHtml     = isReadOnly ? '' : `<span class="gf-chip-grip" aria-hidden="true">${GF_SVG_GRIP_SM}</span>`;
    const customLabel  = workload.customLabel || label;

    // Build effort tooltip (impl + design hours for this workload's quantity)
    const effortCount  = Math.max(0, parseInt(qty, 10) || 0);
    const implH        = effortCount > 0 ? blockHours(effortConfig.implementationBlocks, effortCount) : 0;
    const designH      = effortCount > 0 ? blockHours(effortConfig.designBlocksVbr ?? effortConfig.implementationBlocks, effortCount) : 0;
    const effortTip    = effortCount > 0
        ? `${customLabel}\nImpl: ~${implH}h  ·  Design: ~${designH}h`
        : customLabel;

    const siteBadgeHtml = showSiteBadge && workload.siteName
        ? `<span class="s2-wl-chip-site">${escapeHtml(workload.siteName)}</span>`
        : '';

    return `<span class="s2-wl-chip ${chipClass}${siteBadgeHtml ? ' s2-wl-chip--has-site' : ''}"
                  data-s2-chip="${escapeHtml(String(workload.rowId))}"
                  ${isReadOnly ? 'data-s2-readonly="1"' : ''}
                  title="${escapeHtml(effortTip)}">
        ${gripHtml}
        <span class="s2-wl-chip-icon" aria-hidden="true">${icon}</span>
        <span class="s2-wl-chip-label">${escapeHtml(customLabel)}</span>
        ${qtyText ? `<span class="s2-wl-chip-qty">${escapeHtml(qtyText)}</span>` : ''}
        ${siteBadgeHtml}
    </span>`;
}

/**
 * Returns source workloads that should be shown as read-only "protected" chips
 * inside an existing VBR lane (workloads whose backup job links to that server).
 * TODO: Wire up sourceWorkloads[].jobs[].managedByServerId → protected chip display (Phase 3+).
 */
function getS2ProtectedJobsForLane(_lane) {
    return [];
}

/** Returns VB365 source workloads that appear as protected chips in a VB365 lane.
 *  TODO: Wire up sourceWorkloads[].jobs[].managedByServerId → protected chip display (Phase 3+). */
function getS2ProtectedVb365JobsForLane(_lane) {
    return [];
}

/** Renders a backup job chip for display inside a Step 2 lane.
 *  Pass draggable=true for VB365 lanes where jobs can be reassigned between servers.
 *  Pass draggable=false (default) for VBR lanes where jobs are informational only. */
function buildS2ProtectedJobChip(job, isMultiSite, draggable = false) {
    const jobTypeDef  = jobTypeOptions.find((t) => t.value === job.jobType);
    const jobLabel    = jobTypeDef ? jobTypeDef.label : 'Backup Job';
    const icon        = getGfChipIcon(job.workloadType);
    const unit        = getGfWorkloadUnit(job.workloadType);
    const qty         = job.count || 0;
    const qtyText     = qty > 0 ? `× ${qty}${unit ? ' ' + unit : ''}` : '';
    const displayLabel = job.customLabel || jobLabel;
    const siteBadge   = isMultiSite && job.siteName
        ? `<span class="s2-wl-chip-site">${escapeHtml(job.siteName)}</span>`
        : '';
    const gripHtml    = draggable
        ? `<span class="s2-wl-chip-grip" aria-hidden="true">${GF_SVG_GRIP_SM || ''}</span>`
        : '';
    const readonlyAttr = draggable ? '' : 'data-s2-readonly="1"';
    return `<span class="s2-wl-chip s2-wl-chip--protected${siteBadge ? ' s2-wl-chip--has-site' : ''}"
                  data-s2-chip="${S2_JOB_ID_OFFSET + (job.id || 0)}"
                  ${readonlyAttr}
                  title="${escapeHtml(displayLabel)}${qty > 0 ? `\n${qty}${unit ? ' ' + unit : ''}` : ''}">
        ${gripHtml}
        <span class="s2-wl-chip-icon" aria-hidden="true">${icon}</span>
        <span class="s2-wl-chip-label">${escapeHtml(displayLabel)}</span>
        ${qtyText ? `<span class="s2-wl-chip-qty">${escapeHtml(qtyText)}</span>` : ''}
        ${siteBadge}
    </span>`;
}

function buildS2LaneMarkup(lane, isMultiSite = false) {
    const typeLabel     = lane.type === 'existing' ? 'Existing' : 'New';
    const typeClass     = lane.type === 'existing' ? 's2-lane-type--existing' : 's2-lane-type--new';
    const siteTag       = lane.siteName
        ? (lane.step2Only
            ? `<span class="s2-lane-site-tag s2-lane-site-tag--editable" contenteditable="true" spellcheck="false" role="textbox" aria-label="Site name" title="Click to rename this site" data-s2-site-name="${lane.siteId}">${escapeHtml(lane.siteName)}</span>`
            : `<span class="s2-lane-site-tag">${escapeHtml(lane.siteName)}</span>`)
        : '';
    const assignedWl    = getS2AssignedWorkloads(lane.laneId);

    const assignedHtml  = assignedWl.map((w) => buildS2WorkloadChip(w, 'assigned', isMultiSite)).join('');
    const dropContent   = assignedHtml;
    const dropIsEmpty   = !assignedHtml;

    const emptyWarnClass = '';

    // Config labels (used in both collapsed summary and expanded controls)
    const deployLabel = lane.type === 'new' ? (lane.deploymentType === 'physical' ? 'Physical' : 'Virtual') : '';
    const osLabel     = lane.type === 'new' ? (lane.osPlatform === 'linux' ? 'Linux VSA' : 'Windows') : '';
    const vsaHaLabel  = (lane.type === 'new' && lane.osPlatform === 'linux') ? (lane.vsaHa ? ' · VSA HA' : '') : '';

    // Deployment controls — only on new VBR lanes; hidden behind a toggle by default
    const vsaHaHtml = (lane.osPlatform === 'linux')
        ? `<div class="s2-lane-deploy-field">
                <span class="s2-lane-deploy-label">VSA HA</span>
                <div class="s2-lane-deploy-group" role="radiogroup" aria-label="VSA HA">
                    <label class="s2-lane-deploy-opt${!lane.vsaHa ? ' is-selected' : ''}">
                        <input type="radio" name="s2-vsa-ha-${escapeHtml(lane.laneId)}" value="no"
                            data-s2-lane-prop="${escapeHtml(lane.laneId)}" data-s2-prop-key="vsaHa" data-s2-prop-bool="true"
                            ${!lane.vsaHa ? 'checked' : ''}>
                        <span>No</span>
                    </label>
                    <label class="s2-lane-deploy-opt${lane.vsaHa ? ' is-selected' : ''}">
                        <input type="radio" name="s2-vsa-ha-${escapeHtml(lane.laneId)}" value="yes"
                            data-s2-lane-prop="${escapeHtml(lane.laneId)}" data-s2-prop-key="vsaHa" data-s2-prop-bool="true"
                            ${lane.vsaHa ? 'checked' : ''}>
                        <span>Yes</span>
                    </label>
                </div>
            </div>`
        : '';

    const deploymentControlsHtml = lane.type === 'new'
        ? (!!lane.configExpanded
            ? `<div class="s2-lane-deploy-props">
                <div class="s2-lane-deploy-fields">
                    <div class="s2-lane-deploy-field">
                        <span class="s2-lane-deploy-label">Deployment</span>
                        <div class="s2-lane-deploy-group" role="radiogroup" aria-label="Deployment type">
                            <label class="s2-lane-deploy-opt${lane.deploymentType !== 'physical' ? ' is-selected' : ''}">
                                <input type="radio" name="s2-deploy-type-${escapeHtml(lane.laneId)}" value="virtual"
                                    data-s2-lane-prop="${escapeHtml(lane.laneId)}" data-s2-prop-key="deploymentType"
                                    ${lane.deploymentType !== 'physical' ? 'checked' : ''}>
                                <span>Virtual</span>
                            </label>
                            <label class="s2-lane-deploy-opt${lane.deploymentType === 'physical' ? ' is-selected' : ''}">
                                <input type="radio" name="s2-deploy-type-${escapeHtml(lane.laneId)}" value="physical"
                                    data-s2-lane-prop="${escapeHtml(lane.laneId)}" data-s2-prop-key="deploymentType"
                                    ${lane.deploymentType === 'physical' ? 'checked' : ''}>
                                <span>Physical</span>
                            </label>
                        </div>
                    </div>
                    <div class="s2-lane-deploy-field">
                        <span class="s2-lane-deploy-label">OS</span>
                        <div class="s2-lane-deploy-group" role="radiogroup" aria-label="VBR OS platform">
                            <label class="s2-lane-deploy-opt${lane.osPlatform !== 'linux' ? ' is-selected' : ''}">
                                <input type="radio" name="s2-os-platform-${escapeHtml(lane.laneId)}" value="windows"
                                    data-s2-lane-prop="${escapeHtml(lane.laneId)}" data-s2-prop-key="osPlatform"
                                    ${lane.osPlatform !== 'linux' ? 'checked' : ''}>
                                <span>Windows</span>
                            </label>
                            <label class="s2-lane-deploy-opt${lane.osPlatform === 'linux' ? ' is-selected' : ''}">
                                <input type="radio" name="s2-os-platform-${escapeHtml(lane.laneId)}" value="linux"
                                    data-s2-lane-prop="${escapeHtml(lane.laneId)}" data-s2-prop-key="osPlatform"
                                    ${lane.osPlatform === 'linux' ? 'checked' : ''}>
                                <span>Linux (VSA)</span>
                            </label>
                        </div>
                    </div>
                    ${vsaHaHtml}
                </div>
            </div>`
            : `<div class="s2-lane-config-collapsed">
                <button type="button" class="s2-lane-config-btn" data-s2-toggle-config="${escapeHtml(lane.laneId)}"
                        title="Configure deployment type and OS">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                    ${escapeHtml(deployLabel)} · ${escapeHtml(osLabel)}${escapeHtml(vsaHaLabel)}
                </button>
            </div>`)
        : '';

    return `<div class="s2-lane s2-lane--${lane.type}${emptyWarnClass}" data-s2-lane="${escapeHtml(lane.laneId)}">
        <div class="s2-lane-header s2-lane-header--${lane.type}">
            <span class="s2-lane-header-icon" aria-hidden="true">${GF_SVG_SERVER}</span>
            <span class="s2-lane-name-wrap">
                <span class="s2-lane-name" contenteditable="true" spellcheck="false" role="textbox" aria-label="VBR server name" title="Click to rename" data-s2-lane-name="${escapeHtml(lane.laneId)}">${escapeHtml(lane.name)}</span>
                <svg class="s2-lane-name-edit-icon" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                ${siteTag}
            </span>
            <span class="s2-lane-type-tag ${typeClass}">${typeLabel}</span>
            <button type="button" class="s2-lane-remove-btn" data-s2-remove-lane="${escapeHtml(lane.laneId)}" title="Remove this VBR lane">×</button>
        </div>
        ${deploymentControlsHtml}
        <div class="s2-lane-drop${dropIsEmpty ? ' is-empty' : ''}"
             id="s2-drop-${escapeHtml(lane.laneId)}"
             data-s2-drop-lane="${escapeHtml(lane.laneId)}">
            ${dropContent || `<span class="s2-lane-empty-hint">Drop workloads here</span>`}
        </div>
        ${(() => {
            const protectedJobs = getS2ProtectedJobsForLane(lane);
            if (!protectedJobs.length) return '';
            return `<div class="s2-lane-protected">
                <span class="s2-lane-protected-label">Current jobs on this server</span>
                <div class="s2-lane-protected-chips">
                    ${protectedJobs.map((j) => buildS2ProtectedJobChip(j, isMultiSite)).join('')}
                </div>
            </div>`;
        })()}
    </div>`;
}

function buildS2ReusePicker() {
    const available = getS2ReuseableVbrs();
    if (available.length === 0) {
        return `<div class="s2-picker-panel">
            <p class="s2-picker-empty">No remaining VBR servers from Step 1 to reuse.</p>
            <button type="button" class="s2-picker-cancel-btn" data-s2-picker-cancel>Cancel</button>
        </div>`;
    }
    return `<div class="s2-picker-panel" data-s2-picker="reuse">
        <p class="s2-picker-label">Select an existing VBR from Step 1:</p>
        <div class="s2-picker-options">
            ${available.map((sv) => `
                <button type="button" class="s2-picker-option-btn" data-s2-reuse-vbr="${escapeHtml(sv.instanceId)}">
                    <span class="s2-picker-option-name">${escapeHtml(sv.name || 'VBR Server')}</span>
                    <span class="s2-picker-option-site">${escapeHtml(sv.siteName)}</span>
                </button>`).join('')}
            <button type="button" class="s2-picker-cancel-btn" data-s2-picker-cancel>Cancel</button>
        </div>
    </div>`;
}


/** Returns total impl hours for all workloads assigned to a lane. */
function getS2LaneImplHours(laneId) {
    const workloads = getS2AssignedWorkloads(laneId);
    return workloads.reduce((sum, w) => {
        const count = typeof w.protectedTb === 'number'
            ? Math.round(w.protectedTb)
            : Math.max(0, parseInt(w.count, 10) || 0);
        return sum + blockHours(effortConfig.implementationBlocks, count);
    }, 0);
}

/** Returns total assigned workload count across all lanes. */
function getS2TotalAssignedCount() {
    return step2VbrLanes.reduce((sum, lane) => sum + getS2AssignedWorkloads(lane.laneId).length, 0);
}

/**
 * Builds an overall progress bar spanning all assignable product sections.
 * Only shown when 2+ product types have workloads to assign (otherwise the
 * per-section pill is sufficient).
 */
function buildS2OverallProgress() {
    const vbrTotal    = getS2AllUnprotectedWorkloads().length;
    const vbrAssigned = vbrTotal - getS2UnassignedWorkloads().length;

    let cloudTotal = 0; let cloudAssigned = 0;
    for (const p of Object.keys(CLOUD_PROVIDER_CONFIGS)) {
        const all = getS2CloudAllWorkloads(p);
        cloudTotal    += all.length;
        cloudAssigned += all.length - getS2CloudUnassignedWorkloads(p).length;
    }

    const vb365All      = getS2Vb365Workloads();
    const vb365Total    = vb365All.length;
    const vb365Assigned = vb365Total - vb365All.filter(w => !workloadAssignments[w.rowId]).length;

    const grandTotal    = vbrTotal + cloudTotal + vb365Total;
    const grandAssigned = vbrAssigned + cloudAssigned + vb365Assigned;

    // Only render when multiple product sections are present and there's something to assign
    const activeSections = [vbrTotal, cloudTotal, vb365Total].filter(n => n > 0).length;
    if (activeSections < 2 || grandTotal === 0) return '';

    const pct     = Math.min(100, Math.round((grandAssigned / grandTotal) * 100));
    const allDone = grandAssigned >= grandTotal;

    if (allDone) {
        return `<div class="s2-overall-progress s2-overall-progress--done">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
            All ${grandTotal} workloads placed
        </div>`;
    }
    return `<div class="s2-overall-progress">
        <span class="s2-overall-progress-label">${grandAssigned} of ${grandTotal} workloads placed</span>
        <span class="s2-overall-progress-track"><span class="s2-overall-progress-fill" style="width:${pct}%"></span></span>
    </div>`;
}

/** Builds the summary footer for the Step 2 canvas (now a no-op — replaced by section headers). */
function buildS2SummaryBar(_hW) {
    return '';
}

/** Builds a single VBfCloud appliance lane card. */
function buildS2CloudLaneMarkup(lane, isMultiSite) {
    const cfg        = CLOUD_PROVIDER_CONFIGS[lane.cloudProvider];
    const assignedWl = getS2CloudAssignedWorkloads(lane.laneId);
    const assigned   = assignedWl.map((w) => buildS2WorkloadChip(w, 'assigned', isMultiSite)).join('');
    const dropIsEmpty = !assigned;
    const siteTag    = lane.siteName ? `<span class="s2-lane-site-tag">${escapeHtml(lane.siteName)}</span>` : '';
    return `<div class="s2-lane s2-lane--cloud s2-lane--cloud-${escapeHtml(lane.cloudProvider)}" data-s2-cloud-lane="${escapeHtml(lane.laneId)}">
        <div class="s2-lane-header s2-lane-header--cloud s2-lane-header--cloud-${escapeHtml(lane.cloudProvider)}">
            <span class="s2-lane-header-icon" aria-hidden="true">${GF_SVG_SERVER}</span>
            <span class="s2-lane-name-wrap">
                <span class="s2-lane-name" contenteditable="true" spellcheck="false" role="textbox"
                      aria-label="${escapeHtml(cfg.serverLabel)} name" title="Click to rename"
                      data-s2-lane-name="${escapeHtml(lane.laneId)}">${escapeHtml(lane.name)}</span>
                <svg class="s2-lane-name-edit-icon" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                ${siteTag}
            </span>
            <span class="s2-lane-type-tag s2-lane-type--cloud s2-lane-type--cloud-${escapeHtml(lane.cloudProvider)}">${escapeHtml(cfg.shortLabel)}</span>
            <button type="button" class="s2-lane-remove-btn" data-s2-remove-cloud-lane="${escapeHtml(lane.laneId)}" title="Remove this appliance">×</button>
        </div>
        <div class="s2-lane-drop${dropIsEmpty ? ' is-empty' : ''}"
             id="s2-cloud-drop-${escapeHtml(lane.laneId)}"
             data-s2-cloud-drop-lane="${escapeHtml(lane.laneId)}">
            ${assigned || `<span class="s2-lane-empty-hint">Drop workloads here</span>`}
        </div>
    </div>`;
}

/** Builds the full tray + lanes + add-button section for one cloud provider. Returns '' if no workloads. */
function buildS2CloudSectionMarkup(provider, isMultiSite) {
    const cfg = CLOUD_PROVIDER_CONFIGS[provider];
    if (!cfg) return '';
    const allWl     = getS2CloudAllWorkloads(provider);
    if (allWl.length === 0) return '';

    const unassigned = getS2CloudUnassignedWorkloads(provider);
    const lanes      = step2CloudLanes.filter((l) => l.cloudProvider === provider);
    const hasLanes   = lanes.length > 0;
    const allDone    = unassigned.length === 0;

    // Section header with status pill — red when no lanes exist (nowhere to assign), amber when partially done
    const cloudSectionHeader = buildS2SectionHeader(
        GF_SVG_SERVER,
        `${cfg.fullLabel || cfg.shortLabel} mapping`,
        allWl.length - unassigned.length,
        allWl.length,
        !hasLanes && unassigned.length > 0 /* noTarget */
    );

    const cloudTrayActionHint = hasLanes ? 'drag onto an appliance below' : `add a ${escapeHtml(cfg.shortLabel)} appliance below, then drag`;
    // Tray: only shown when items remain — section pill handles the "all done" confirmation
    const trayHtml = !allDone
        ? `<div class="s2-canvas-tray s2-canvas-tray--cloud s2-canvas-tray--pending s2-canvas-tray--cloud-${escapeHtml(provider)}">
               <div class="s2-tray-header">
                   <span class="s2-tray-title s2-tray-title--cloud s2-tray-title--cloud-${escapeHtml(provider)}">${unassigned.length} workload${unassigned.length !== 1 ? 's' : ''} unassigned — ${cloudTrayActionHint}</span>
               </div>
               <div class="s2-tray-chips"
                    id="s2-cloud-tray-${escapeHtml(provider)}"
                    data-s2-cloud-drop-lane=""
                    data-s2-cloud-provider="${escapeHtml(provider)}">
                   ${unassigned.map((w) => buildS2WorkloadChip(w, 'tray', isMultiSite)).join('')}
               </div>
           </div>`
        : '';

    const lanesHtml = hasLanes
        ? `<div class="s2-canvas-lanes">
               ${lanes.map((lane) => buildS2CloudLaneMarkup(lane, isMultiSite)).join('')}
           </div>`
        : `<div class="s2-canvas-lanes s2-canvas-lanes--empty" id="s2-cloud-lanes-${escapeHtml(provider)}">
               <div class="s2-empty-state-actions">
                   <button type="button" class="s2-add-vbr-btn s2-add-vbr-btn--new s2-add-cloud-btn--${escapeHtml(provider)}"
                           data-s2-add-cloud-lane="${escapeHtml(provider)}">
                       + Add ${escapeHtml(cfg.shortLabel)} appliance
                   </button>
               </div>
           </div>`;

    // Add-appliance row only appears once lanes exist — the empty state card already has the button.
    const addRowHtml = hasLanes
        ? `<div class="s2-add-vbr-row">
               <button type="button" class="s2-add-vbr-btn s2-add-vbr-btn--new s2-add-cloud-btn--${escapeHtml(provider)}"
                       data-s2-add-cloud-lane="${escapeHtml(provider)}">
                   + Add ${escapeHtml(cfg.shortLabel)} appliance
               </button>
           </div>`
        : '';

    return `<div class="s2-cloud-section s2-cloud-section--${escapeHtml(provider)}">
        ${cloudSectionHeader}
        ${trayHtml}
        ${lanesHtml}
        ${addRowHtml}
    </div>`;
}

/** Returns existing VB365 named server instances not yet added as VB365 lanes. */
function getS2ReuseableVb365s() {
    const usedIds = new Set(step2Vb365Lanes.filter(l => l.type === 'existing').map(l => l.sourceInstanceId));
    return sites.flatMap(site =>
        (site.veeamServers || [])
            .filter(sv => sv.type === 'vb365' && !usedIds.has(sv.instanceId))
            .map(sv => ({ instanceId: sv.instanceId, name: sv.name || 'VB365 Server', siteId: site.id, siteName: site.name || `Site ${site.id}` }))
    );
}

/** Adds an existing VB365 server from Step 1 as a Step 2 lane. */
function addS2ExistingVb365Lane(instanceId) {
    const entry = getS2ReuseableVb365s().find(e => e.instanceId === instanceId);
    if (!entry) return;
    step2Vb365Lanes.push({
        laneId: String(s2NextLaneId++),
        type: 'existing',
        sourceInstanceId: entry.instanceId,
        name: `${entry.name} (Reused)`,
        siteId: entry.siteId,
        siteName: entry.siteName,
    });
    window.step2Vb365Lanes = step2Vb365Lanes;
    s2ActiveVb365Picker = null;
    renderStep2Canvas();
    scheduleRenderEstimate();
}

/** Adds a new VB365 server lane for future-state deployment. */
function addS2NewVb365Lane(siteId) {
    const site = getSiteById(siteId);
    const newCount = step2Vb365Lanes.filter(l => l.type === 'new').length + 1;
    step2Vb365Lanes.push({
        laneId: String(s2NextLaneId++),
        type: 'new',
        name: `New VB365 Server ${newCount}`,
        siteId: siteId,
        siteName: site ? (site.name || `Site ${siteId}`) : String(siteId),
    });
    window.step2Vb365Lanes = step2Vb365Lanes;
    s2ActiveVb365Picker = null;
    renderStep2Canvas();
    scheduleRenderEstimate();
}

/** Removes a VB365 lane and un-assigns its workloads. */
function removeS2Vb365Lane(laneId) {
    step2Vb365Lanes = step2Vb365Lanes.filter(l => l.laneId !== laneId);
    window.step2Vb365Lanes = step2Vb365Lanes;
    Object.keys(workloadAssignments).forEach(rowId => {
        if (workloadAssignments[rowId] === laneId) delete workloadAssignments[rowId];
    });
    renderStep2Canvas();
    scheduleRenderEstimate();
    emit(BUS_EVENTS.VALIDATION_SYNC_NEEDED);
}

function buildS2Vb365Section(isMultiSite) {
    const vb365Workloads = getS2Vb365Workloads();
    const existingVb365Servers = sites.flatMap(s =>
        (s.veeamServers || []).filter(sv => sv.type === 'vb365').map(sv => ({ server: sv, site: s }))
    );
    const hasContent = vb365Workloads.length > 0 || existingVb365Servers.length > 0 || step2Vb365Lanes.length > 0;
    if (!hasContent) return { html: '', addRow: '' };

    const escHtml = escapeHtml;

    // Tray: M365 workloads not yet assigned to any VB365 lane
    const unassigned = vb365Workloads.filter(w => !workloadAssignments[w.rowId]);
    const hasVb365Lanes = step2Vb365Lanes.length > 0;
    // Protected jobs (M365 jobs already linked to a VB365 server in Step 1) are auto-placed
    // and never appear in the draggable tray — count them toward the header totals so the
    // pill shows "All X placed" correctly even when all placements are via Step 1 links.
    const protectedVb365JobCount = step2Vb365Lanes.reduce(
        (sum, lane) => sum + getS2ProtectedVb365JobsForLane(lane).length, 0
    );
    const vb365HeaderTotal    = vb365Workloads.length + protectedVb365JobCount;
    const vb365HeaderAssigned = (vb365Workloads.length - unassigned.length) + protectedVb365JobCount;
    // Always show the section header — even when there are no VB365 workloads to assign,
    // the label anchors the Reuse/Add buttons that follow.
    const vb365SectionHeader = buildS2SectionHeader(
        GF_SVG_SERVER,
        'VB365 server mapping',
        vb365HeaderAssigned,
        vb365HeaderTotal,
        !hasVb365Lanes && unassigned.length > 0 /* noTarget */
    );
    const vb365TrayActionHint = hasVb365Lanes ? 'drag onto a server below' : 'add a VB365 server below, then drag';
    // Tray: only shown when items remain — section pill handles the "all done" confirmation
    const trayHtml = (vb365Workloads.length > 0 && unassigned.length > 0)
        ? `<div class="s2-canvas-tray s2-canvas-tray--pending">
               <div class="s2-tray-header">
                   <span class="s2-tray-title">${unassigned.length} workload${unassigned.length !== 1 ? 's' : ''} unassigned — ${vb365TrayActionHint}</span>
               </div>
               <div class="s2-tray-chips" id="s2-vb365-tray">${unassigned.map(w => buildS2WorkloadChip(w, 'tray', isMultiSite)).join('')}</div>
           </div>`
        : '';

    // Lane cards
    const lanesHtml = step2Vb365Lanes.map(lane => {
        const typeTag = lane.type === 'existing' ? 'EXISTING' : 'NEW';
        const typeClass = lane.type === 'existing' ? 's2-lane-type--existing' : 's2-lane-type--vb365';
        const sitePart = isMultiSite ? ` <span class="s2-lane-name-site">(${escHtml(lane.siteName)})</span>` : '';
        const assigned = vb365Workloads.filter(w => workloadAssignments[w.rowId] === lane.laneId);
        const assignedChips = assigned.map(w => buildS2WorkloadChip(w, 'assigned', isMultiSite)).join('');
        return `<div class="s2-lane s2-lane--vb365" data-s2-vb365-lane="${escHtml(lane.laneId)}">
            <div class="s2-lane-header s2-lane-header--vb365">
                <span class="s2-lane-header-icon" aria-hidden="true">${GF_SVG_SERVER}</span>
                <span class="s2-lane-name-wrap">
                    <span class="s2-lane-name" contenteditable="true" spellcheck="false" data-s2-vb365-lane-name="${escHtml(lane.laneId)}">${escHtml(lane.name)}</span>${sitePart}
                </span>
                <span class="s2-lane-type-tag ${typeClass}">${typeTag}</span>
                <button type="button" class="s2-lane-remove-btn" data-s2-remove-vb365-lane="${escHtml(lane.laneId)}" title="Remove this lane">×</button>
            </div>
            <div class="s2-lane-drop" id="s2-vb365-drop-${escHtml(lane.laneId)}" data-s2-vb365-drop-lane="${escHtml(lane.laneId)}">
                ${assignedChips}
                ${(() => {
                const protectedJobs = getS2ProtectedVb365JobsForLane(lane);
                if (!protectedJobs.length) return '';
                return `<div class="s2-lane-protected s2-lane-protected--inline">
                    <span class="s2-lane-protected-label">Current jobs on this server</span>
                    <div class="s2-lane-protected-chips">
                        ${protectedJobs.map((j) => buildS2ProtectedJobChip(j, isMultiSite, true)).join('')}
                    </div>
                </div>`;
            })()}
        </div>
        </div>`;
    }).join('');

    // Reuse picker
    const reuseableVb365s = getS2ReuseableVb365s();
    const reusePickerHtml = s2ActiveVb365Picker === 'reuse' ? (
        reuseableVb365s.length === 0
            ? `<div class="s2-picker-panel">
                   <p class="s2-picker-empty">No remaining VB365 servers from Step 1 to reuse.</p>
                   <button type="button" class="s2-picker-cancel-btn" data-s2-vb365-picker-cancel>Cancel</button>
               </div>`
            : `<div class="s2-picker-panel" data-s2-picker="reuse">
                   <p class="s2-picker-label">Select an existing VB365 from Step 1:</p>
                   <div class="s2-picker-options">
                       ${reuseableVb365s.map(e => `
                           <button type="button" class="s2-picker-option-btn" data-s2-reuse-vb365="${escHtml(e.instanceId)}">
                               <span class="s2-picker-option-name">${escHtml(e.name)}</span>
                               ${isMultiSite ? `<span class="s2-picker-option-site">${escHtml(e.siteName)}</span>` : ''}
                           </button>`).join('')}
                       <button type="button" class="s2-picker-cancel-btn" data-s2-vb365-picker-cancel>Cancel</button>
                   </div>
               </div>`)
        : '';

    // New VB365 picker — mirrors buildS2NewVbrForm() structure and classes
    const newPickerHtml = s2ActiveVb365Picker === 'new' ? `
        <div class="s2-picker-panel" data-s2-picker="new">
            <p class="s2-picker-label">Which site will this VB365 be deployed to?</p>
            <div class="s2-site-picker-options">
                ${sites.filter(s => !s.step2Only).map(s => `
                    <button type="button" class="s2-site-picker-btn" data-s2-new-vb365-site="${s.id}">
                        ${escHtml(s.name || `Site ${s.id}`)}
                    </button>`).join('')}
                <button type="button" class="s2-site-picker-btn s2-site-picker-btn--add-site" data-s2-new-site-vb365>
                    + New site
                </button>
                <button type="button" class="s2-picker-cancel-btn" data-s2-vb365-picker-cancel>Cancel</button>
            </div>
        </div>` : '';

    // Add row
    const addRowHtml = `
        <div class="s2-add-vbr-row">
            ${s2ActiveVb365Picker
                ? (reusePickerHtml || newPickerHtml)
                : `${reuseableVb365s.length > 0
                    ? `<button type="button" class="s2-add-vbr-btn s2-add-vbr-btn--reuse" data-s2-open-vb365-picker="reuse">
                           ↩ Reuse existing VB365
                       </button>`
                    : ''}
                   <button type="button" class="s2-add-vbr-btn s2-add-vbr-btn--new" data-s2-open-vb365-picker="new">+ Add new VB365</button>`
            }
        </div>`;

    return {
        html: `<div class="s2-cloud-section s2-vb365-section">
            ${vb365SectionHeader}
            ${trayHtml}
            ${lanesHtml}
        </div>`,
        addRow: addRowHtml,
    };
}



/**
 * Builds read-only Kasten K10 auto-assignment cards.
 * One card per existing Kasten server in site.veeamServers, PLUS one card per
 * unprotected Kubernetes workload chip (representing a new Kasten deployment).
 * `existingServers` = [{ server, site }], `workloads` = normalised unprotected K8s chips.
 */
function buildS2KastenCards(workloads, isMultiSite, existingServers = []) {
    const cards = [];

    // One card per existing Kasten server
    for (const { server, site } of existingServers) {
        const sitePart = (isMultiSite && site) ? ` · ${escapeHtml(site.name || `Site ${site.id}`)}` : '';
        cards.push(`<div class="s2-lane s2-lane--kasten">
            <div class="s2-lane-header s2-lane-header--kasten">
                <span class="s2-lane-header-icon" aria-hidden="true">${GF_SVG_SERVER}</span>
                <span class="s2-lane-name-wrap">
                    <span class="s2-lane-name" style="pointer-events:none;">${escapeHtml(server.name)}${sitePart}</span>
                </span>
                <span class="s2-lane-type-tag s2-lane-type--existing">EXISTING</span>
            </div>
            <div class="s2-lane-kasten-note">Existing Kasten K10 — manages one Kubernetes cluster</div>
            <div class="s2-lane-drop"></div>
        </div>`);
    }

    // One card per unprotected Kubernetes workload (new Kasten deployment needed)
    for (const w of workloads) {
        const sitePart = (isMultiSite && w.siteName) ? ` · ${escapeHtml(w.siteName)}` : '';
        cards.push(`<div class="s2-lane s2-lane--kasten">
            <div class="s2-lane-header s2-lane-header--kasten">
                <span class="s2-lane-header-icon" aria-hidden="true">${GF_SVG_SERVER}</span>
                <span class="s2-lane-name-wrap">
                    <span class="s2-lane-name" style="pointer-events:none;">Kasten K10${sitePart}</span>
                </span>
                <span class="s2-lane-type-tag s2-lane-type--kasten">AUTO</span>
            </div>
            <div class="s2-lane-kasten-note">Automatically deployed — one Kasten K10 instance per Kubernetes cluster</div>
            <div class="s2-lane-drop">
                ${buildS2WorkloadChip(w, 'protected', isMultiSite)}
            </div>
        </div>`);
    }

    return cards.join('');
}

function buildStep2CanvasMarkup() {
    const { hW, hP, hC } = getCanvasEngagementFlags();
    const allUnprotected  = getS2AllUnprotectedWorkloads();
    const unassigned      = getS2UnassignedWorkloads();

    // Detect multi-site so chips can show a site badge only when needed
    const activeSiteNames = new Set(sites.map((s) => s.name || `Site ${s.id}`));
    const isMultiSite = activeSiteNames.size > 1;

    const hasLanes        = step2VbrLanes.length > 0;
    const unassignedCount = unassigned.length;
    // Tray: only shown when items remain — section pill handles the "all done" confirmation
    const trayActionHint  = hasLanes ? 'drag onto a server below' : 'add a VBR server below, then drag';
    const trayHtml = (allUnprotected.length > 0 && unassignedCount > 0)
        ? `<div class="s2-canvas-tray s2-canvas-tray--pending">
               <div class="s2-tray-header">
                   <span class="s2-tray-title">${unassignedCount} workload${unassignedCount !== 1 ? 's' : ''} unassigned — ${trayActionHint}</span>
               </div>
               <div class="s2-tray-chips"
                    id="s2-tray-chips"
                    data-s2-drop-lane="">
                   ${unassigned.map((w) => buildS2WorkloadChip(w, 'tray', isMultiSite)).join('')}
               </div>
           </div>`
        : '';

    // Add-VBR action row (only relevant when there are unprotected workloads)
    const reuseAvailable = getS2ReuseableVbrs().length > 0;

    const lanesHtml = hasLanes
        ? `<div class="s2-canvas-lanes" id="s2-lanes">
               ${step2VbrLanes.map((lane) => buildS2LaneMarkup(lane, isMultiSite)).join('')}
           </div>`
        : `<div class="s2-canvas-lanes s2-canvas-lanes--empty" id="s2-lanes">
               ${s2ActivePicker
                   ? (s2ActivePicker === 'reuse' ? buildS2ReusePicker() : buildS2NewVbrForm())
                   : `<div class="s2-empty-state-actions">
                          <button type="button" class="s2-add-vbr-btn s2-add-vbr-btn--new" data-s2-open-picker="new">+ Add new VBR</button>
                          ${reuseAvailable ? `<button type="button" class="s2-add-vbr-btn s2-add-vbr-btn--reuse" data-s2-open-picker="reuse">↩ Reuse existing VBR</button>` : ''}
                      </div>`
               }
           </div>`;
    // Only show the bottom add-row when lanes already exist — the empty state handles the zero-lane case.
    // When a picker is active, render it in-place of the buttons so it appears in the same location.
    const addRowHtml = hasLanes ? `
        <div class="s2-add-vbr-row">
            ${s2ActivePicker
                ? (s2ActivePicker === 'reuse' ? buildS2ReusePicker() : buildS2NewVbrForm())
                : `${reuseAvailable
                    ? `<button type="button" class="s2-add-vbr-btn s2-add-vbr-btn--reuse" data-s2-open-picker="reuse">
                           ↩ Reuse existing VBR
                       </button>`
                    : ''}
                   <button type="button" class="s2-add-vbr-btn s2-add-vbr-btn--new" data-s2-open-picker="new">+ Add new VBR</button>`
            }
        </div>` : '';

    // Cloud provider sections (one per provider that has unprotected workloads)
    const cloudSectionsHtml = Object.keys(CLOUD_PROVIDER_CONFIGS)
        .map((p) => buildS2CloudSectionMarkup(p, isMultiSite))
        .join('');

    // VB365 section — interactive lane mapping
    // addRow is returned separately so it renders outside the section wrapper,
    // matching the VBR pattern (+ Add new VBR sits at canvas level, not inside a container).
    const { html: vb365SectionHtml, addRow: vb365AddRowHtml } = buildS2Vb365Section(isMultiSite);

    // Kasten K10 — one card per existing Kasten server + one per unprotected Kubernetes workload chip
    const kastenWorkloads = getS2KastenWorkloads();
    const existingKastenServers = sites.flatMap((s) =>
        (s.veeamServers || []).filter((sv) => sv.type === 'kasten').map((sv) => ({ server: sv, site: s }))
    );
    const kastenCardsHtml = (kastenWorkloads.length > 0 || existingKastenServers.length > 0)
        ? buildS2KastenCards(kastenWorkloads, isMultiSite, existingKastenServers) : '';

    let headingText = '';
    if (hW && hC)       headingText = 'Map workloads to backup servers';
    else if (hW && !hC) headingText = 'Map workloads to backup servers';
    else if (hP && hC)  headingText = 'Existing backup environment';

    // Protected jobs (Step 1 jobs linked to an existing VBR) are auto-placed as read-only
    // chips and never appear in the draggable tray — include them in the header totals.
    const protectedVbrJobCount = step2VbrLanes.reduce(
        (sum, lane) => sum + getS2ProtectedJobsForLane(lane).length, 0
    );
    const vbrHeaderTotal    = allUnprotected.length + protectedVbrJobCount;
    const vbrHeaderAssigned = (allUnprotected.length - unassigned.length) + protectedVbrJobCount;
    // VBR section — show only when there's something VBR-relevant (draggable workloads, protected jobs, or existing lanes)
    const hasVbrContent = vbrHeaderTotal > 0 || step2VbrLanes.length > 0;
    const vbrSectionHeader = hasVbrContent
        ? buildS2SectionHeader(GF_SVG_SERVER, 'VBR server mapping', vbrHeaderAssigned, vbrHeaderTotal)
        : '';

    return `<div class="s2-canvas">
        ${hasVbrContent ? vbrSectionHeader : ''}
        ${hasVbrContent ? trayHtml : ''}
        ${hasVbrContent ? lanesHtml : ''}
        ${hasVbrContent ? addRowHtml : ''}
        ${cloudSectionsHtml}
        ${vb365SectionHtml}
        ${vb365AddRowHtml}
        ${kastenCardsHtml}
    </div>`;
}

/**
 * Removes stale workloadAssignment entries — those pointing to a laneId that no
 * longer exists in any lane array. This can happen after import/export round-trips
 * or when a lane is removed while assignments remain.
 */
function pruneStaleAssignments() {
    const validLaneIds = new Set([
        ...step2VbrLanes.map((l) => l.laneId),
        ...step2CloudLanes.map((l) => l.laneId),
        ...step2Vb365Lanes.map((l) => l.laneId),
    ]);
    Object.keys(workloadAssignments).forEach((rowId) => {
        if (!validLaneIds.has(workloadAssignments[rowId])) {
            delete workloadAssignments[rowId];
        }
    });
}

function renderStep2Canvas() {
    const el      = document.getElementById('s2-assignment-canvas');
    const section = document.getElementById('design-platform-section');
    if (!el) return;

    pruneStaleAssignments();

    const { hW, hP, hC } = getCanvasEngagementFlags();
    const shouldShow = hW || hP || hC;

    // Show/hide the Platform section wrapper
    if (section) section.hidden = !shouldShow;

    el.hidden = !shouldShow;
    if (!shouldShow) {
        destroyStep2Sortables();
        if (_s2CollapseConfigHandler) {
            document.removeEventListener('click', _s2CollapseConfigHandler);
            _s2CollapseConfigHandler = null;
        }
        return;
    }

    el.innerHTML = buildStep2CanvasMarkup();
    wireStep2CanvasEvents();
    initStep2Sortables();
}

function initStep2Sortables() {
    destroyStep2Sortables();

    // ── VBR sortables ────────────────────────────────────────────────────────
    if (step2VbrLanes.length > 0) {
        const onVbrChipMoved = (evt) => {
            const rowId = parseInt(evt.item.dataset.s2Chip, 10);
            if (isNaN(rowId)) return;
            const toLaneId = evt.to.dataset.s2DropLane;
            if (toLaneId) {
                workloadAssignments[rowId] = toLaneId;
                // Collapse any open config panels — user is clearly done configuring
                step2VbrLanes.forEach((l) => { if (l.type === 'new') l.configExpanded = false; });
            } else {
                delete workloadAssignments[rowId];
            }
            renderStep2Canvas();
            emit(BUS_EVENTS.STEP2_CANVAS_CHANGED);
        };

        const trayEl = document.getElementById('s2-tray-chips');
        if (trayEl) {
            s2SortableTray = Sortable.create(trayEl, {
                group: 's2-workloads', animation: 150,
                draggable: '.s2-wl-chip:not([data-s2-readonly])',
                ghostClass: 's2-chip-ghost', chosenClass: 's2-chip-chosen',
                onEnd: onVbrChipMoved,
            });
            window.s2SortableTray = s2SortableTray;
        }

        step2VbrLanes.forEach((lane) => {
            const dropEl = document.getElementById(`s2-drop-${lane.laneId}`);
            if (!dropEl) return;
            s2SortableLanes.push(Sortable.create(dropEl, {
                group: 's2-workloads', animation: 150,
                draggable: '.s2-wl-chip:not([data-s2-readonly])',
                ghostClass: 's2-chip-ghost', chosenClass: 's2-chip-chosen',
                onEnd: onVbrChipMoved,
            }));
        });
    }

    // ── Cloud sortables (one group per provider, isolated from VBR) ──────────
    Object.keys(CLOUD_PROVIDER_CONFIGS).forEach((provider) => {
        const cfg = CLOUD_PROVIDER_CONFIGS[provider];
        const providerLanes = step2CloudLanes.filter((l) => l.cloudProvider === provider);
        if (providerLanes.length === 0) return; // no lanes yet for this provider

        const onCloudChipMoved = (evt) => {
            const rowId = parseInt(evt.item.dataset.s2Chip, 10);
            if (isNaN(rowId)) return;
            const toLaneId = evt.to.dataset.s2CloudDropLane;
            if (toLaneId) {
                workloadAssignments[rowId] = toLaneId;
            } else {
                delete workloadAssignments[rowId];
            }
            renderStep2Canvas();
            emit(BUS_EVENTS.STEP2_CANVAS_CHANGED);
        };

        const cloudTrayEl = document.getElementById(`s2-cloud-tray-${provider}`);
        if (cloudTrayEl) {
            s2SortableLanes.push(Sortable.create(cloudTrayEl, {
                group: cfg.sortGroup, animation: 150,
                draggable: '.s2-wl-chip:not([data-s2-readonly])',
                ghostClass: 's2-chip-ghost', chosenClass: 's2-chip-chosen',
                onEnd: onCloudChipMoved,
            }));
        }

        providerLanes.forEach((lane) => {
            const dropEl = document.getElementById(`s2-cloud-drop-${lane.laneId}`);
            if (!dropEl) return;
            s2SortableLanes.push(Sortable.create(dropEl, {
                group: cfg.sortGroup, animation: 150,
                draggable: '.s2-wl-chip:not([data-s2-readonly])',
                ghostClass: 's2-chip-ghost', chosenClass: 's2-chip-chosen',
                onEnd: onCloudChipMoved,
            }));
        });
    });

    // ── VB365 sortables ────────────────────────────────────────────────────
    if (step2Vb365Lanes.length > 0) {
        const onVb365ChipMoved = (evt) => {
            const rowId = parseInt(evt.item.dataset.s2Chip, 10);
            if (isNaN(rowId)) return;
            const toLaneId = evt.to.dataset.s2Vb365DropLane || evt.to.id?.replace('s2-vb365-drop-', '');
            if (toLaneId && toLaneId !== 's2-vb365-tray') {
                workloadAssignments[rowId] = toLaneId;
            } else {
                delete workloadAssignments[rowId];
            }
            renderStep2Canvas();
            emit(BUS_EVENTS.STEP2_CANVAS_CHANGED);
        };

        const vb365TrayEl = document.getElementById('s2-vb365-tray');
        if (vb365TrayEl) {
            s2SortableLanes.push(Sortable.create(vb365TrayEl, {
                group: 's2-vb365-workloads', animation: 150,
                draggable: '.s2-wl-chip:not([data-s2-readonly])',
                ghostClass: 's2-chip-ghost', chosenClass: 's2-chip-chosen',
                onEnd: onVb365ChipMoved,
            }));
        }
        step2Vb365Lanes.forEach(lane => {
            const dropEl = document.getElementById(`s2-vb365-drop-${lane.laneId}`);
            if (!dropEl) return;
            s2SortableLanes.push(Sortable.create(dropEl, {
                group: 's2-vb365-workloads', animation: 150,
                draggable: '.s2-wl-chip:not([data-s2-readonly])',
                ghostClass: 's2-chip-ghost', chosenClass: 's2-chip-chosen',
                onEnd: onVb365ChipMoved,
            }));
        });
    }
}

function wireStep2CanvasEvents() {
    // Click-outside: collapse any expanded new-VBR config panels
    if (_s2CollapseConfigHandler) document.removeEventListener('click', _s2CollapseConfigHandler);
    _s2CollapseConfigHandler = (e) => {
        if (e.target.closest('.s2-lane-deploy-props')) return;
        if (e.target.closest('[data-s2-toggle-config]')) return;
        if (!step2VbrLanes.some((l) => l.type === 'new' && l.configExpanded)) return;
        step2VbrLanes.forEach((l) => { if (l.type === 'new') l.configExpanded = false; });
        renderStep2Canvas();
    };
    document.addEventListener('click', _s2CollapseConfigHandler);

    // Toggle picker panel open/close
    document.querySelectorAll('[data-s2-open-picker]').forEach((btn) => {
        btn.addEventListener('click', () => {
            const which = btn.dataset.s2OpenPicker;
            s2ActivePicker = s2ActivePicker === which ? null : which;
            renderStep2Canvas();
        });
    });

    // Cancel / close picker
    document.querySelectorAll('[data-s2-picker-cancel]').forEach((btn) => {
        btn.addEventListener('click', () => {
            s2ActivePicker = null;
            renderStep2Canvas();
        });
    });

    // Reuse existing VBR from Step 1
    document.querySelectorAll('[data-s2-reuse-vbr]').forEach((btn) => {
        btn.addEventListener('click', () => {
            addS2ExistingVbrLane(btn.dataset.s2ReuseVbr);
        });
    });

    // New VBR form: picking a site creates the lane (name auto-generated)
    document.querySelectorAll('[data-s2-new-vbr-site]').forEach((btn) => {
        btn.addEventListener('click', () => {
            const siteId = parseInt(btn.dataset.s2NewVbrSite, 10);
            addS2NewVbrLane('', siteId);
        });
    });


    // step2Only site name editing — inline rename on the site tag badge
    document.querySelectorAll('[data-s2-site-name]').forEach((el) => {
        const siteId = parseInt(el.dataset.s2SiteName, 10);
        el.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') { e.preventDefault(); el.blur(); }
            if (e.key === 'Escape') {
                const site = getSiteById(siteId);
                el.textContent = (site && site.name) || `Site ${siteId}`;
                el.blur();
            }
        });
        el.addEventListener('blur', () => {
            const newName = el.textContent.trim();
            const site = getSiteById(siteId);
            if (!site) return;
            site.name = newName;
            // Sync the name onto all VBR lanes pointing at this site
            step2VbrLanes.forEach((l) => { if (l.siteId === siteId) l.siteName = newName || `Site ${siteId}`; });
            renderStep2Canvas();
            scheduleRenderEstimate();
        });
    });

    // New VBR form: "+ New site" — creates a future-state-only site (hidden from Step 1) then assigns the VBR lane
    document.querySelectorAll('[data-s2-new-site-vbr]').forEach((btn) => {
        btn.addEventListener('click', () => {
            const newSite = window.addSite({ step2Only: true });
            if (newSite) addS2NewVbrLane('', newSite.id);
        });
    });

    // Lane name inline editing — commit on blur or Enter (VBR + cloud lanes)
    document.querySelectorAll('[data-s2-lane-name]').forEach((span) => {
        span.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') { e.preventDefault(); span.blur(); }
        });
        span.addEventListener('blur', () => {
            const laneId = span.dataset.s2LaneName;
            const lane   = step2VbrLanes.find((l) => l.laneId === laneId)
                        || step2CloudLanes.find((l) => l.laneId === laneId);
            if (!lane) return;
            const newName = span.textContent.trim();
            if (newName) {
                lane.name = newName;
            } else {
                span.textContent = lane.name;
            }
        });
    });

    // Add a new cloud appliance lane
    document.querySelectorAll('[data-s2-add-cloud-lane]').forEach((btn) => {
        btn.addEventListener('click', () => {
            const provider = btn.dataset.s2AddCloudLane;
            // Default to the first site that has workloads for this provider
            const site = sites.find((s) =>
                (s.unprotectedWorkloads || []).some((w) => getVbfCloudProvider(w.workloadType) === provider)
            ) || sites[0];
            if (!site) return;
            addS2CloudLane(provider, site.id);
            scheduleRenderEstimate();
        });
    });

    // Remove a cloud appliance lane
    document.querySelectorAll('[data-s2-remove-cloud-lane]').forEach((btn) => {
        btn.addEventListener('click', () => {
            removeS2CloudLane(btn.dataset.s2RemoveCloudLane);
            scheduleRenderEstimate();
        });
    });

    // ── VB365 lane events ──────────────────────────────────────────────────
    // Toggle VB365 picker panel open/close
    document.querySelectorAll('[data-s2-open-vb365-picker]').forEach(btn => {
        btn.addEventListener('click', () => {
            const which = btn.dataset.s2OpenVb365Picker;
            s2ActiveVb365Picker = s2ActiveVb365Picker === which ? null : which;
            renderStep2Canvas();
        });
    });

    // Cancel VB365 picker
    document.querySelectorAll('[data-s2-vb365-picker-cancel]').forEach(btn => {
        btn.addEventListener('click', () => {
            s2ActiveVb365Picker = null;
            renderStep2Canvas();
        });
    });

    // Reuse existing VB365
    document.querySelectorAll('[data-s2-reuse-vb365]').forEach(btn => {
        btn.addEventListener('click', () => {
            addS2ExistingVb365Lane(btn.dataset.s2ReuseVb365);
        });
    });

    // New VB365 on existing site
    document.querySelectorAll('[data-s2-new-vb365-site]').forEach(btn => {
        btn.addEventListener('click', () => {
            addS2NewVb365Lane(parseInt(btn.dataset.s2NewVb365Site, 10));
        });
    });

    // New VB365 on a new step2-only site
    document.querySelectorAll('[data-s2-new-site-vb365]').forEach(btn => {
        btn.addEventListener('click', () => {
            const newSite = window.addSite({ step2Only: true });
            if (newSite) addS2NewVb365Lane(newSite.id);
        });
    });

    // Remove VB365 lane
    document.querySelectorAll('[data-s2-remove-vb365-lane]').forEach(btn => {
        btn.addEventListener('click', () => {
            removeS2Vb365Lane(btn.dataset.s2RemoveVb365Lane);
        });
    });

    // VB365 lane name inline editing
    document.querySelectorAll('[data-s2-vb365-lane-name]').forEach(span => {
        span.addEventListener('keydown', e => {
            if (e.key === 'Enter') { e.preventDefault(); span.blur(); }
        });
        span.addEventListener('blur', () => {
            const laneId = span.dataset.s2Vb365LaneName;
            const lane = step2Vb365Lanes.find(l => l.laneId === laneId);
            if (!lane) return;
            const newName = span.textContent.trim();
            lane.name = newName || lane.name;
            if (!newName) span.textContent = lane.name;
        });
    });

    // Config toggle — show/hide deploy props for new VBR lanes
    document.querySelectorAll('[data-s2-toggle-config]').forEach((btn) => {
        btn.addEventListener('click', () => {
            const laneId = btn.dataset.s2ToggleConfig;
            const lane   = step2VbrLanes.find((l) => l.laneId === laneId);
            if (!lane) return;
            lane.configExpanded = !lane.configExpanded;
            renderStep2Canvas();
        });
    });

    // Remove a lane (and un-assign its workloads)
    document.querySelectorAll('[data-s2-remove-lane]').forEach((btn) => {
        btn.addEventListener('click', () => {
            removeS2Lane(btn.dataset.s2RemoveLane);
            scheduleRenderEstimate();
        });
    });

    // New VBR lane property controls (deploymentType, osPlatform, vsaHa)
    document.querySelectorAll('[data-s2-lane-prop]').forEach((input) => {
        input.addEventListener('change', () => {
            const laneId  = input.dataset.s2LaneProp;
            const propKey = input.dataset.s2PropKey;
            const isBool  = input.dataset.s2PropBool === 'true';
            const lane    = step2VbrLanes.find((l) => l.laneId === laneId);
            if (!lane || !propKey) return;
            lane[propKey] = isBool ? (input.value === 'yes') : input.value;
            // When switching away from Linux, clear VSA HA
            if (propKey === 'osPlatform' && input.value !== 'linux') {
                lane.vsaHa = false;
            }
            // Re-render so is-selected classes update; no full canvas re-init needed
            renderStep2Canvas();
            scheduleRenderEstimate();
        });
    });
}

/**
 * Auto-adds existing VBR lanes for any backup job that is linked to a VBR
 * server via managedByInstanceId in Step 1, if that server is not already
 * represented as a lane.
 *
 * Called when entering Step 2 so that users who linked jobs to a named VBR
 * server in Step 1 see that server pre-populated as a lane, without having to
 * manually click "↩ Reuse existing VBR".
 *
 * Does NOT call renderStep2Canvas() — the caller is expected to render after.
 */
/**
 * Mirrors autoAddLinkedVbrLanes() for VB365.
 * When a source workload is linked to a named VB365 server via managedByServerId,
 * auto-create an existing-type VB365 lane on Step 2 entry so it appears placed.
 */
function autoAddLinkedVb365Lanes() {
    const linkedIds = new Set();
    for (const site of sites) {
        for (const sw of (site.sourceWorkloads || [])) {
            if (!isVb365ManagedWorkload(sw.workloadValue)) continue;
            for (const j of (sw.jobs || [])) {
                if (j.managedByServerId) linkedIds.add(j.managedByServerId);
            }
        }
    }
    if (!linkedIds.size) return;

    const existingLaneIds = new Set(
        step2Vb365Lanes
            .filter((l) => l.type === 'existing' && l.sourceInstanceId)
            .map((l) => l.sourceInstanceId)
    );

    for (const instanceId of linkedIds) {
        if (existingLaneIds.has(instanceId)) continue;
        let entry = getS2ReuseableVb365s().find((e) => e.instanceId === instanceId);
        if (!entry) {
            // Already used or not in reuseables — search all named servers directly
            for (const site of sites) {
                const sv = (site.veeamServers || []).find(
                    (s) => s.type === 'vb365' && s.instanceId === instanceId
                );
                if (sv) {
                    entry = { instanceId: sv.instanceId, name: sv.name || 'VB365 Server', siteId: site.id, siteName: site.name || `Site ${site.id}` };
                    break;
                }
            }
        }
        if (!entry) continue;
        const newLaneId = String(s2NextLaneId++);
        step2Vb365Lanes.push({
            laneId:           newLaneId,
            type:             'existing',
            sourceInstanceId: entry.instanceId,
            name:             entry.name ? `${entry.name} (Reused)` : 'Reused VB365 Server',
            siteId:           entry.siteId,
            siteName:         entry.siteName,
        });
        window.step2Vb365Lanes = step2Vb365Lanes;
        // Auto-assign VB365 source workloads whose managedByServerId matches this lane
        for (const site of sites) {
            for (const sw of (site.sourceWorkloads || [])) {
                if (!isVb365ManagedWorkload(sw.workloadValue)) continue;
                const normalized = normalizeSourceWorkloadForS2(sw, site);
                if (normalized.managedByServerId === entry.instanceId) {
                    workloadAssignments[normalized.rowId] = newLaneId;
                }
            }
        }
        existingLaneIds.add(instanceId);
    }
}

function autoAddLinkedVbrLanes() {
    // Collect all unique server IDs from sourceWorkloads
    const linkedIds = new Set();
    for (const site of sites) {
        for (const sw of (site.sourceWorkloads || [])) {
            if (isVb365ManagedWorkload(sw.workloadValue) || isVbfCloudManagedWorkload(sw.workloadValue)) continue;
            for (const j of (sw.jobs || [])) {
                if (j.managedByServerId) linkedIds.add(j.managedByServerId);
            }
        }
    }
    if (!linkedIds.size) return;

    // Determine which instanceIds are already represented as existing-type lanes
    const existingLaneIds = new Set(
        step2VbrLanes
            .filter((l) => l.type === 'existing' && l.sourceInstanceId)
            .map((l) => l.sourceInstanceId)
    );

    // Add a lane for each linked VBR not yet in the canvas — reuse addS2ExistingVbrLane
    // but suppress the per-call renderStep2Canvas by temporarily patching the render.
    // Simpler: push the lane directly (same logic as addS2ExistingVbrLane, minus the render).
    for (const instanceId of linkedIds) {
        if (existingLaneIds.has(instanceId)) continue;
        // Resolve the instanceId to an entry. Handles three cases:
        //  1. Named veeamServers instance (e.g. 'vs-1') — found in getS2ReuseableVbrs()
        //  2. EE slot using current convention: 'env-vbr-{siteId}-{slot}' — also in getS2ReuseableVbrs()
        //  3. Legacy 'ee-vbr-{slot}' IDs written by source-workload-pane.js before the
        //     ID convention was fixed — translate to 'env-vbr-{siteId}-{slot}' by scanning sites.
        const entry = getS2ReuseableVbrs().find((e) => e.instanceId === instanceId)
            ?? (() => {
                // Resolve named veeamServers entries not yet in reuseables
                for (const site of sites) {
                    const sv = (site.veeamServers || []).find(
                        (s) => s.type === 'vbr' && s.instanceId === instanceId
                    );
                    if (sv) return {
                        instanceId: sv.instanceId,
                        name: sv.name || 'VBR Server',
                        siteId: site.id,
                        siteName: site.name || `Site ${site.id}`,
                    };
                }
                // Handle legacy 'ee-vbr-{slot}' IDs — remap to correct env-vbr-{siteId}-{slot}
                const legacyMatch = instanceId.match(/^ee-vbr-(\d+)$/);
                if (legacyMatch) {
                    const slot = parseInt(legacyMatch[1], 10);
                    for (const site of sites) {
                        const correctedId = `env-vbr-${site.id}-${slot}`;
                        const reuseable = getS2ReuseableVbrs().find((e) => e.instanceId === correctedId);
                        if (reuseable) return reuseable;
                    }
                }
                return null;
            })();
        if (!entry) continue;
        const newLaneId = String(s2NextLaneId++);
        step2VbrLanes.push({
            laneId:           newLaneId,
            type:             'existing',
            sourceInstanceId: entry.instanceId,
            name:             entry.name ? `${entry.name} (Reused)` : 'Reused VBR Server',
            siteId:           entry.siteId,
            siteName:         entry.siteName,
        });
        window.step2VbrLanes = step2VbrLanes;
        // Auto-assign source workloads whose managedByServerId matches this lane
        // so they appear placed in the lane's drop zone without requiring manual drag.
        for (const site of sites) {
            for (const sw of (site.sourceWorkloads || [])) {
                if (isVb365ManagedWorkload(sw.workloadValue) || isVbfCloudManagedWorkload(sw.workloadValue)) continue;
                const normalized = normalizeSourceWorkloadForS2(sw, site);
                if (normalized.managedByServerId === entry.instanceId) {
                    workloadAssignments[normalized.rowId] = newLaneId;
                }
            }
        }
        existingLaneIds.add(instanceId); // prevent double-add if called more than once
    }
}

/**
 * Restore Step 2 canvas state from a serialised snapshot (e.g. on import).
 * Resets current state first, then repopulates the module-level let variables.
 * autoAddLinkedVbrLanes() checks sourceInstanceId against existing lanes before
 * adding, so it will not create duplicates when the user advances to Step 2.
 */
function applyStep2CanvasState(savedState) {
    if (!savedState || typeof savedState !== 'object') return;
    resetStep2Canvas();
    const { vbrLanes = [], cloudLanes = [], vb365Lanes = [], assignments = {}, nextLaneId = 1 } = savedState;
    step2VbrLanes.push(...vbrLanes.map((l) => ({ ...l })));
    window.step2VbrLanes = step2VbrLanes;
    step2CloudLanes.push(...cloudLanes.map((l) => ({ ...l })));
    window.step2CloudLanes = step2CloudLanes;
    step2Vb365Lanes.push(...vb365Lanes.map(l => ({ ...l })));
    window.step2Vb365Lanes = step2Vb365Lanes;
    Object.assign(workloadAssignments, assignments);
    s2NextLaneId = Number.isInteger(nextLaneId) && nextLaneId > 1 ? nextLaneId : 1;
}

export { renderStep2Canvas, resetStep2Canvas, autoAddLinkedVbrLanes, autoAddLinkedVb365Lanes, getS2UnassignedWorkloads, getS2AllUnprotectedWorkloads, getS2CloudUnassignedWorkloads, getS2CloudAllWorkloads, getS2Vb365UnassignedWorkloads, getS2Vb365Workloads, getS2Vb365BlocksNavigation, applyStep2CanvasState };
